/* SmartEvaluate — RFP Conversion Preview modal.
 *
 * Shows the consultant a structured preview of how PRIVA's extracted
 * intelligence will be mapped onto the existing SmartEvaluate questionnaire,
 * lets them override key fields, and finalises the conversion which then
 * runs the existing SmartEvaluate engines (recommendations / ROI / etc.).
 *
 * The PRIVA Extracted Intelligence section stays mounted underneath this
 * dialog — nothing here mutates the raw extraction.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  AlertCircle,
  Building2,
  Boxes,
  Plug,
  Server,
  TrendingUp,
  Loader2,
  Sparkles,
  Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  previewRfpConversion,
  convertRfpToSmartEvaluate,
} from "../lib/rfpApi";

const SCENARIO_OPTIONS = [
  { value: "greenfield", label: "Greenfield" },
  { value: "brownfield", label: "Brownfield" },
  { value: "optimization", label: "Optimization" },
  { value: "migration", label: "Migration" },
];

const WH_TYPE_OPTIONS = [
  { value: "cdc", label: "Central Distribution Center" },
  { value: "rdc", label: "Regional Distribution Center" },
  { value: "manufacturing", label: "Manufacturing Plant Warehouse" },
  { value: "ecommerce", label: "E-commerce Fulfillment Center" },
  { value: "spare_parts", label: "Spare Parts / Service Warehouse" },
  { value: "satellite", label: "Small / Satellite Warehouse" },
];

const COMPLEXITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const COMPLEXITY_DIMENSIONS = [
  { key: "warehouse_complexity", label: "Warehouse Complexity" },
  { key: "integration_complexity", label: "Integration Complexity" },
  { key: "automation_complexity", label: "Automation Complexity" },
  { key: "inventory_complexity", label: "Inventory Complexity" },
  { key: "throughput_complexity", label: "Throughput Complexity" },
];

const DEPLOYMENT_OPTIONS = [
  { value: "embedded_ewm", label: "Embedded EWM" },
  { value: "decentralized_ewm", label: "Decentralized EWM" },
  { value: "hybrid", label: "Hybrid" },
  { value: "cloud", label: "Cloud" },
  { value: "on_premise", label: "On-Premise" },
  { value: "to_be_decided", label: "To Be Decided" },
];

const PROCESS_LABELS = {
  inbound: "Inbound",
  outbound: "Outbound / Picking",
  inventory: "Inventory & Movements",
  quality: "Quality Management",
  returns: "Returns",
  automation: "Automation",
  integration: "Integration",
  reporting: "Reporting & KPIs",
};

function bandTone(v) {
  if (v === "high") return "bg-[#FEE2E2] text-[#991B1B] border-[#991B1B]/15";
  if (v === "medium") return "bg-[#FEF3C7] text-[#92400E] border-[#92400E]/15";
  if (v === "low") return "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15";
  return "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]";
}

function PreviewSection({ icon: Icon, title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="border border-[#E5E7EB] rounded-sm bg-white"
    >
      <div className="px-4 py-2.5 border-b border-[#E5E7EB] bg-[#F8FAFC] flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.75} />
        <h4 className="text-[12px] font-semibold text-[#111827] uppercase tracking-wider"
          style={{ fontFamily: "Chivo, sans-serif" }}>
          {title}
        </h4>
      </div>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

export default function RfpConversionPreview({
  open,
  onOpenChange,
  assessmentId,
  onConverted,
}) {
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({
    scenario: null,
    sites: [],
    complexity: {},
    deployment: null,
  });
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);

  // Initial fetch when modal opens.
  useEffect(() => {
    if (!open || !assessmentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOverrides({ scenario: null, sites: [], complexity: {}, deployment: null });
    setNotes("");
    previewRfpConversion(assessmentId, {})
      .then((d) => {
        if (cancelled) return;
        setPreview(d.preview);
        setOverrides({
          scenario: d.preview?.scenario?.detected || null,
          sites: (d.preview?.sites || []).map((s) => ({
            site_id: s.site_id,
            type: s.type,
          })),
          complexity: { ...(d.preview?.complexity || {}) },
          deployment: d.preview?.deployment?.detected || null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to build conversion preview.";
        setError(detail);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assessmentId]);

  const sapLandscape = preview?.sap_landscape || {};
  const processScope = preview?.process_scope || {};
  const warnings = preview?.warnings || [];

  const handleConvert = async () => {
    if (!assessmentId) return;
    setConverting(true);
    try {
      const payload = {
        scenario: overrides.scenario,
        sites: overrides.sites,
        complexity: overrides.complexity,
        deployment: overrides.deployment,
      };
      await convertRfpToSmartEvaluate(assessmentId, payload, notes);
      toast.success("Bid Analysis created — engines run complete.");
      onConverted?.();
      onOpenChange(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to convert.";
      toast.error(detail);
    } finally {
      setConverting(false);
    }
  };

  const updateSiteType = (siteId, newType) => {
    setOverrides((o) => ({
      ...o,
      sites: o.sites.map((s) =>
        s.site_id === siteId ? { ...s, type: newType } : s
      ),
    }));
  };
  const updateComplexity = (dim, val) => {
    setOverrides((o) => ({
      ...o,
      complexity: { ...o.complexity, [dim]: val },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="rfp-conversion-preview-modal"
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#6D28D9]" strokeWidth={1.75} />
            Bid Analysis Preview
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#4B5563] leading-relaxed">
            Review and adjust how PRIVA's extracted intelligence will map into
            the Bid Analysis inputs. Confirm to create the Bid Analysis and
            generate the bid recommendation, commercial &amp; ROI view and Bid Summary Report.
            The raw PRIVA extraction is preserved.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12" data-testid="rfp-conversion-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#6D28D9] mr-2" />
            <span className="text-sm text-[#4B5563]">Building preview…</span>
          </div>
        )}

        {error && !loading && (
          <div
            data-testid="rfp-conversion-error"
            className="bg-red-50 border border-red-200 rounded-sm px-4 py-3 flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <span className="text-[12.5px] text-red-700">{error}</span>
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-4">
            {/* A — Scenario */}
            <PreviewSection
              icon={Building2}
              title="A. Assessment Scenario"
              testId="rfp-conversion-scenario"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <Select
                  value={overrides.scenario || preview.scenario.detected}
                  onValueChange={(v) =>
                    setOverrides((o) => ({ ...o, scenario: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="rfp-conversion-scenario-select"
                    className="w-56 h-9 text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCENARIO_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[12px] text-[#6B7280] flex-1">
                  Detected: <strong>{preview.scenario.detected}</strong> — {preview.scenario.reason}
                </p>
              </div>
            </PreviewSection>

            {/* B — Warehouse Network */}
            <PreviewSection
              icon={Building2}
              title="B. Warehouse Network Structure"
              testId="rfp-conversion-sites"
            >
              <div className="space-y-2.5">
                {(preview.sites || []).map((s) => {
                  const current =
                    overrides.sites.find((o) => o.site_id === s.site_id)?.type ||
                    s.type;
                  return (
                    <div
                      key={s.site_id}
                      data-testid={`rfp-conversion-site-${s.site_id}`}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 border border-[#E5E7EB] rounded-sm px-3 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#1F2937] font-medium truncate">{s.name}</p>
                        <p className="text-[11px] text-[#9CA3AF]">
                          {s.raw_summary || "Site type not specified by RFP"}
                        </p>
                      </div>
                      <Select
                        value={current}
                        onValueChange={(v) => updateSiteType(s.site_id, v)}
                      >
                        <SelectTrigger
                          data-testid={`rfp-conversion-site-type-${s.site_id}`}
                          className="w-64 h-8 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WH_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </PreviewSection>

            {/* C — Process Scope */}
            <PreviewSection
              icon={Boxes}
              title="C. Process Scope Mapping"
              testId="rfp-conversion-scope"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(processScope).map(([k, v]) => (
                  <div
                    key={k}
                    className="border border-[#E5E7EB] rounded-sm px-3 py-2"
                    data-testid={`rfp-conversion-scope-${k}`}
                  >
                    <div className="flex items-center gap-2">
                      {v.in_scope ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#065F46]" strokeWidth={2} />
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-[#9CA3AF]" strokeWidth={2} />
                      )}
                      <span className="text-sm font-medium text-[#1F2937]">
                        {PROCESS_LABELS[k] || k}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${v.in_scope ? "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15" : "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]"}`}>
                        {v.in_scope ? "In Scope" : "Out of Scope"}
                      </span>
                    </div>
                    {v.signals && v.signals.length > 0 && (
                      <ul className="mt-1.5 ml-5 list-disc text-[11px] text-[#6B7280] space-y-0.5">
                        {v.signals.slice(0, 3).map((sig, i) => (
                          <li key={i} className="line-clamp-2">{sig}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </PreviewSection>

            {/* D — Volume & Complexity */}
            <PreviewSection
              icon={TrendingUp}
              title="D. Volume & Complexity Mapping"
              testId="rfp-conversion-complexity"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {COMPLEXITY_DIMENSIONS.map((d) => {
                  const detected = preview.complexity?.[d.key] || "medium";
                  const current = overrides.complexity[d.key] || detected;
                  return (
                    <div
                      key={d.key}
                      data-testid={`rfp-conversion-complexity-${d.key}`}
                      className="flex items-center justify-between gap-2 border border-[#E5E7EB] rounded-sm px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-sm text-[#1F2937]">{d.label}</span>
                        <span
                          className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${bandTone(detected)}`}
                        >
                          {detected}
                        </span>
                      </div>
                      <Select
                        value={current}
                        onValueChange={(v) => updateComplexity(d.key, v)}
                      >
                        <SelectTrigger
                          data-testid={`rfp-conversion-complexity-select-${d.key}`}
                          className="w-28 h-8 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPLEXITY_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </PreviewSection>

            {/* E — SAP Landscape */}
            <PreviewSection
              icon={Plug}
              title="E. SAP Landscape Mapping"
              testId="rfp-conversion-sap"
            >
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 text-sm">
                <Field label="SAP Integration Required" value={sapLandscape.sap_required ? "Yes" : "No"} />
                <Field label="EDI Required" value={sapLandscape.edi_required ? "Yes" : "No"} />
                <Field label="3PL Integration" value={sapLandscape.three_pl ? "Yes" : "No"} />
                <Field
                  label="Integration Methods"
                  value={(sapLandscape.integration_methods || []).join(", ") || "—"}
                />
                <div className="sm:col-span-2">
                  <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
                    Integration Objects
                  </dt>
                  <dd className="mt-0.5 text-sm text-[#1F2937]">
                    {(sapLandscape.integration_objects || []).join(" · ") || "—"}
                  </dd>
                </div>
                {(sapLandscape.explicit_exclusions || []).length > 0 && (
                  <div className="sm:col-span-2">
                    <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
                      Explicit Exclusions
                    </dt>
                    <dd className="mt-0.5 text-[12px] text-[#6B7280]">
                      {sapLandscape.explicit_exclusions.join(" · ")}
                    </dd>
                  </div>
                )}
              </dl>
            </PreviewSection>

            {/* F — Deployment */}
            <PreviewSection
              icon={Server}
              title="F. Deployment Strategy (initial mapping)"
              testId="rfp-conversion-deployment"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <Select
                  value={overrides.deployment || preview.deployment?.detected || "to_be_decided"}
                  onValueChange={(v) =>
                    setOverrides((o) => ({ ...o, deployment: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="rfp-conversion-deployment-select"
                    className="w-56 h-9 text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPLOYMENT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[12px] text-[#6B7280] flex-1">
                  {preview.deployment?.reason}
                </p>
              </div>
              <p className="mt-2 text-[11px] text-[#9CA3AF] italic flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                This is an initial mapping only — the recommendation engine
                will produce the final deployment recommendation per site.
              </p>
            </PreviewSection>

            {/* G — Warnings */}
            {warnings.length > 0 && (
              <PreviewSection
                icon={AlertCircle}
                title="G. Missing Information & Assumptions"
                testId="rfp-conversion-warnings"
              >
                <ul className="ml-5 list-disc text-[12.5px] text-[#92400E] space-y-1">
                  {warnings.slice(0, 15).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
                {warnings.length > 15 && (
                  <p className="mt-2 text-[11px] text-[#6B7280]">
                    +{warnings.length - 15} more warning{warnings.length - 15 === 1 ? "" : "s"} preserved on the record.
                  </p>
                )}
              </PreviewSection>
            )}

            {/* Optional notes */}
            <div>
              <label className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
                Conversion Notes (optional)
              </label>
              <Textarea
                data-testid="rfp-conversion-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Document any assumptions or override rationale for the audit trail."
                rows={2}
                className="mt-1 text-sm rounded-sm border-[#D1D5DB]"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            data-testid="rfp-conversion-cancel-btn"
            onClick={() => onOpenChange(false)}
            disabled={converting}
            className="rounded-sm h-9 px-4 text-xs font-medium border-[#D1D5DB] text-[#374151] hover:bg-[#F8FAFC]"
          >
            Cancel
          </Button>
          <Button
            data-testid="rfp-conversion-confirm-btn"
            onClick={handleConvert}
            disabled={loading || converting || !preview}
            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-9 px-5 text-xs font-semibold rounded-sm shadow-sm disabled:opacity-50"
          >
            {converting ? (
              <Loader2 className="mr-1.5 w-3.5 h-3.5 animate-spin" />
            ) : (
              <ArrowRight className="mr-1.5 w-3.5 h-3.5" />
            )}
            {converting ? "Converting…" : "Confirm & Convert"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-[#1F2937]">{value || "—"}</dd>
    </div>
  );
}
